import { useState } from 'react';

import { Label, Select } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { shuffle } from 'lodash-es';

import styles from './ManualUploadDE.module.css';
import { fetchPutAndGetState } from '../../../../../api/lendingApi/lendingApiMutations';
import {
  useGetStateQuery,
  useSplitTestBranch,
} from '../../../../../api/lendingApi/lendingApiQueries';
import { ISOLATED_SPLIT_TESTS } from '../../../../../constants';
import { useContextStateKey } from '../../../../../hooks/useContextStateKey';
import { useIsInDEOpenBankingCopyTest } from '../../../../../hooks/useIsInDEOpenBankingCopyTest';
import { useOffersDE } from '../../../../../hooks/useOffers/useOffersDE';
import { TOfferDataRequirement } from '../../../../../types';
import DocumentRequirement from '../../../DocumentRequirement/DocumentRequirement';

const SharedPDFHelpText = () => {
  const { splitTestBranch } = useSplitTestBranch(
    ISOLATED_SPLIT_TESTS.DE_6M_PDF_BS_TEST,
    { enabled: true, cookieOverride: true },
  );
  const bankStatementDays = splitTestBranch === 'INCLUDE' ? 180 : 90;

  return (
    <>
      <li>
        Laden Sie die Umsätze im PDF-Format mit vollständigem Buchungstext hoch
      </li>
      <li>Jede Datei darf max. 50MB groß sein</li>
      <li>
        Stellen Sie sicher, dass die Dateien nicht passwortgeschützt sind (oder
        teilen Sie das Passwort mit uns)
      </li>
      <li>
        {`Die Konten müssen mindestens die Buchungen der letzten ${bankStatementDays} Tage umfassen.`}
      </li>
      <li>
        Wir benötigen alle Konten, die mehr als 10% Ihres Gesamtumsatzes
        ausmachen.
      </li>
    </>
  );
};

const HelpText = () => {
  const { isIncludedInOBCopyTest } = useIsInDEOpenBankingCopyTest();

  if (isIncludedInOBCopyTest) {
    return (
      <p>
        Wenn Sie Probleme haben, Ihre Auszüge über Ihre Bank zu übermitteln,
        können Sie diese als PDF hochladen. Die Bearbeitung der Dokumente wird
        länger dauern. Ihr Antrag kann sich um bis zu 4 Arbeitstage verzögern.
      </p>
    );
  }

  return (
    <ul>
      <li>
        Sie können sich die Kontoauszüge in Ihrem Online Banking herunterladen
      </li>
      <SharedPDFHelpText />
    </ul>
  );
};

type TProps = {
  title: string;
  isSubmitted: boolean;
  status?: TOfferDataRequirement['actions'][number]['status'];
};

type TReasonSelectProps = {
  onReasonChange: (value: string) => void;
};

const ReasonSelect = ({ onReasonChange }: TReasonSelectProps) => {
  const { isIncludedInOBCopyTest } = useIsInDEOpenBankingCopyTest();

  const getManualUploadReasons = (excludeOpenBankingReferences: boolean) => {
    const reasons = [
      { value: 1, text: t`I do not have online banking.` },
      { value: 2, text: t`I find uploading PDFs easier.` },
      { value: 3, text: t`I could not find my bank.` },
      {
        value: 4,
        text: excludeOpenBankingReferences
          ? t`I do not trust online banking.`
          : t`I do not trust the Open Banking solution.`,
      },
      {
        value: 5,
        text: excludeOpenBankingReferences
          ? t`I feel that I share more information than necessary.`
          : t`I feel that I share more information than necessary with Open Banking.`,
      },
      { value: 6, text: t`I do not wish to answer the question.` },
    ];
    const lastReason = reasons[reasons.length - 1];
    return [...shuffle(reasons.slice(0, -1)), lastReason];
  };

  return (
    <div className={styles.manualReasonsContainer}>
      <Label
        htmlFor="manual-bank-statement-reason"
        className={styles.manualSelectTitle}
      >
        {t`Select an option below to manually upload bank statements`}
      </Label>
      <Select
        defaultValue="none"
        name="manual-bank-statement-reason"
        className={styles.manualSelectInput}
        onChange={(e) => {
          if (e.target.value !== 'none') {
            onReasonChange(e.target.value);
          }
        }}
      >
        <optgroup label="Ich kann mein Bankkonto nicht verknüpfen">
          <option disabled hidden value="none">
            Bitte wählen Sie eine Option aus
          </option>
          {getManualUploadReasons(isIncludedInOBCopyTest).map(
            (manualreason) => (
              <option key={manualreason.value} value={manualreason.text}>
                {manualreason.text}
              </option>
            ),
          )}
        </optgroup>
      </Select>
    </div>
  );
};

const ManualUploadDE = ({ status, isSubmitted }: TProps) => {
  const { state } = useGetStateQuery();
  const stateKey = useContextStateKey();

  const { setIsSyncingState } = useOffersDE();
  const { isIncludedInOBCopyTest } = useIsInDEOpenBankingCopyTest();

  const [showManualUploadButton, setShowManualUploadButton] = useState(false);

  const submitManualReason = async (manualReasonData: string) => {
    setIsSyncingState(true);
    const putStateBody = {
      data: {
        ...state,
        ui: {
          ...state.ui,
          OB_not_linked_reason: manualReasonData,
        },
      },
    };

    // @ts-expect-error: mismatch type
    await fetchPutAndGetState({ body: putStateBody, stateKey });
    setIsSyncingState(false);
  };

  const onReasonChange = (value: string) => {
    setShowManualUploadButton(true);
    submitManualReason(value);
  };

  return (
    <DocumentRequirement
      className={styles.documentRequirement}
      isColumnLayout
      documentType="bank statement"
      isSubmitted={isSubmitted}
      status={status}
      buttonText={<Trans>Upload Bank Statements</Trans>}
      customButton={showManualUploadButton ? null : <></>}
    >
      <>
        <HelpText />
        <ReasonSelect onReasonChange={onReasonChange} />
        {isIncludedInOBCopyTest && showManualUploadButton && (
          <ul className={styles.postSelectionList}>
            <SharedPDFHelpText />
          </ul>
        )}
      </>
    </DocumentRequirement>
  );
};

export default ManualUploadDE;
